// ---------------------------------------------------------------------------
// Types (canonical, no -V2 suffix)
// ---------------------------------------------------------------------------

/** Стадия производства проекта — поле `production_status` в теле `/v2/projects`. */
export type ProductionStatus =
    "development" | "pre_production" | "production" | "post_production" | "distribution" | "released";

/** Тип проекта (что снимаем) — поле `type` в теле `/v2/projects`. */
export type ProjectType =
    | "feature_film"
    | "tv_series"
    | "animation"
    | "documentary"
    | "short_film"
    | "commercial"
    | "music_video"
    | "teaser_trailer"
    | "other";

/** Вкладка списка проектов; уезжает в query `tab` запроса `GET /v2/projects`. */
export type ProjectTab = "owned" | "shared" | "archived" | "all";

/** Роль участника проекта. `owner` — создатель, он же единственный неснимаемый. */
export type ProjectMemberRole = "owner" | "admin" | "member";
/** Состояние строки участника: приглашение отправлено, принято либо отозвано. */
export type ProjectMemberStatus = "active" | "invited" | "removed";

/** Участник проекта — элемент ответа `GET /v2/projects/{id}/members` и результат
 * приглашения/изменения роли. Профильные поля (`email`…`image`) сервер резолвит
 * только для принятых участников; у приглашённых они `null`. */
export interface ProjectMember {
    id: string;
    projectId: string;
    userId: string | null;
    invitedEmail: string | null;
    role: string;
    status: ProjectMemberStatus;
    inviteToken: string | null;
    inviteExpiresAt: string | null;
    createdAt: string | null;
    /** Resolved user profile (active members only; null for invited rows). */
    email: string | null;
    name: string | null;
    firstName: string | null;
    surname: string | null;
    image: string | null;
}

/** Счётчики карточки проекта; сервер считает их сам и кладёт в `Project.counters`. */
export interface ProjectCounters {
    scenesTotal: number;
    chatsTotal: number;
    generationsTotal: number;
    activeMembersCount: number;
    completionPct: number;
}

/** Роль медиа проекта: постер (картинка) либо тизер (видео). */
export type ProjectMediaKind = "poster" | "teaser";

/** Медиа проекта — то, что возвращает `POST /v2/projects/{id}/media` и что лежит
 * в `Project.media`. */
export interface ProjectMedia {
    id: string;
    projectId: string;
    mediaType: ProjectMediaKind;
    s3Url: string;
    s3Key: string | null;
    mimeType: string | null;
    fileSize: number | null;
    sortOrder: number;
    createdAt: string | null;
}

/** Проект целиком — ответ `GET /v2/projects/{id}` и всех мутаций проекта
 * (создание, правка, архивация). */
export interface Project {
    id: string;
    name: string;
    description: string | null;
    logline: string | null;
    synopsis: string | null;
    productionStatus: ProductionStatus;
    projectType: ProjectType | null;
    startDate: string | null;
    endDate: string | null;
    workspaceId: string;
    ownerId: string | null;
    ownerName: string | null;
    ownerEmail: string | null;
    isArchived: boolean;
    archivedAt: string | null;
    createdAt: string | null;
    counters: ProjectCounters;
    posters: ProjectMedia[];
    teasers: ProjectMedia[];
}

/** Строка списка проектов: подмножество `Project` со счётчиками, которое отдаёт
 * `GET /v2/projects` (полное тело там не приходит). */
export interface ProjectListItem {
    id: string;
    name: string;
    projectType: ProjectType | null;
    productionStatus: ProductionStatus;
    startDate: string | null;
    endDate: string | null;
    workspaceId: string;
    ownerId: string | null;
    ownerName: string | null;
    ownerEmail: string | null;
    isArchived: boolean;
    archivedAt: string | null;
    counters: ProjectCounters;
    posterUrl: string | null;
    createdAt: string | null;
    myRole: ProjectMemberRole | null;
}

/** Ответ `GET /v2/projects`: страница строк плюс общее число для пагинации. */
export interface ProjectListResponse {
    items: ProjectListItem[];
    total: number;
    page: number;
    pageSize: number;
}

/** Параметры запроса `GET /v2/projects` (вкладка, воркспейс, страница, поиск). */
export interface ListProjectsParams {
    tab?: ProjectTab;
    workspaceId?: string;
    status?: readonly string[];
    type?: readonly string[];
    q?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    pageSize?: number;
}

/** Тело `POST /v2/projects`; воркспейс передаётся отдельным query-параметром. */
export interface CreateProjectPayload {
    name: string;
    projectType: ProjectType;
    workspaceId: string;
}

/** Тело `PATCH /v2/projects/{id}` — частичное, присланы только изменённые поля. */
export interface UpdateProjectPayload {
    name?: string;
    description?: string;
    logline?: string;
    synopsis?: string;
    productionStatus?: ProductionStatus;
    projectType?: ProjectType;
    startDate?: string;
    endDate?: string;
}

/** Тело `POST /v2/projects/{id}/media`: файл уже загружен в S3, сюда едет ссылка. */
export interface AttachMediaPayload {
    mediaType: ProjectMediaKind;
    s3Url: string;
    s3Key?: string | null;
    mimeType?: string | null;
    fileSize?: number | null;
}

/** Тело `POST /v2/projects/{id}/members/invite` — список адресов и роль для всех. */
export interface InviteMembersPayload {
    emails: string[];
    role: "admin" | "member";
}

/** Тело `PATCH /v2/projects/{id}/members/{memberId}` — смена роли участника. */
export interface UpdateMemberPayload {
    role?: ProjectMemberRole;
    status?: ProjectMemberStatus;
}

/** Тело `POST /v2/projects/{id}/members/add` — добавление участника воркспейса
 * без письма-приглашения. */
export interface AddExistingMemberPayload {
    userId: string;
    role: "admin" | "member";
}

/** Вид события журнала проекта — поле `event` строки `GET /v2/projects/{id}/log`. */
export type ProjectLogEvent =
    | "project_created"
    | "project_updated"
    | "project_archived"
    | "project_unarchived"
    | "member_added"
    | "member_invited"
    | "member_role_changed"
    | "member_status_changed"
    | "member_removed"
    | "ownership_transferred"
    | "scene_created"
    | "scene_renamed"
    | "scene_archived"
    | "scene_unarchived"
    | "scene_deleted"
    | "file_added"
    | "file_updated"
    | "file_deleted";

/** Строка журнала проекта: кто, что и когда сделал. */
export interface ProjectLogEntry {
    timestamp: string;
    event: ProjectLogEvent;
    actorId: string | null;
    actorName: string | null;
    actorEmail: string | null;
    actorImage: string | null;
    targetEmail: string | null;
    targetUserId: string | null;
    targetName: string | null;
    targetImage: string | null;
    metadata: Record<string, unknown> | null;
}

/** Ответ `GET /v2/projects/{id}/log`: страница строк журнала плюс общее число. */
export interface ProjectLogResponse {
    entries: ProjectLogEntry[];
}

/** Генерация, привязанная к проекту — строка `GET /v2/projects/{id}/generations`. */
export interface ProjectGeneration {
    id: string;
    status: string;
    modelName: string;
    sceneId: string | null;
    createdAt: string;
    durationSeconds: number | null;
    errorMessage: string | null;
}

/** Ответ `GET /v2/projects/{id}/generations`: страница генераций плюс общее число. */
export interface ProjectGenerationListResponse {
    items: ProjectGeneration[];
    total: number;
}

// ---------------------------------------------------------------------------
// Query keys (canonical) — shared across all project api sub-modules.
// ---------------------------------------------------------------------------

export const projectsKeys = {
    all: ["projects"] as const,
    list: (params: ListProjectsParams) => ["projects", "list", params] as const,
    byId: (id: string) => ["projects", "detail", id] as const,
    members: (id: string) => ["projects", "members", id] as const,
    log: (id: string) => ["projects", "log", id] as const,
    generations: (id: string, params?: { limit: number; offset: number }) =>
        ["projects", "generations", id, params] as const,
    files: (id: string) => ["projects", "files", id] as const,
    canAdmin: (id: string) => ["projects", "can-admin", id] as const,
};
